/* @jsx h */
import { defineComponent, h, ref, onMounted, computed } from "vue";
import { ProductCategoryRequirement, Category } from "@lemmonhq/types";
import {
  SelectableListComboBox,
  SelectableItem,
} from "@app/vue/components/index";
import { api } from "@app/data/index";

export type ProductCategoryRequirementEditorProps = {
  value: ProductCategoryRequirement;
  onChange: (requirement: ProductCategoryRequirement) => void;
};

export const ProductCategoryRequirementEditor =
  defineComponent<ProductCategoryRequirementEditorProps>({
    name: "ProductCategoryRequirementEditor",
    props: ["value", "onChange"],
    setup(props) {
      const categories = ref<Category[]>([]);
      const loading = ref(false);

      const loadCategories = async () => {
        if (loading.value || categories.value.length > 0) return;
        try {
          loading.value = true;
          // Note: Using services API as placeholder since categories API doesn't exist yet
          const mockCategories = (await api.getServices())
            .map((el) => el.categories || [])
            .flat();
          categories.value = mockCategories as Category[];
        } catch (error) {
          console.error("Error loading categories:", error);
        } finally {
          loading.value = false;
        }
      };

      onMounted(() => {
        loadCategories();
      });

      const updateField = (
        field: keyof ProductCategoryRequirement,
        value: any,
      ) => {
        props.onChange({
          ...props.value,
          [field]: value,
        });
      };

      // Convert categories to SelectableItem format
      const selectableCategories = computed((): SelectableItem[] =>
        categories.value.map((category) => ({
          id: category.id,
          name: category.name,
        })),
      );

      const handleSelectionChange = (selectedIds: number[]) => {
        updateField("category_ids", selectedIds);
      };

      return () => (
        <div class="card border">
          <div class="card-body p-4">
            <h4 class="card-title text-accent text-lg flex items-center gap-2">
              <div class="w-2 h-2 bg-info rounded-full"></div>
              Category Requirement
            </h4>

            <div class="space-y-4 mt-4">
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">Required Quantity</span>
                </label>
                <input
                  type="number"
                  value={props.value.quantity}
                  onInput={(e) =>
                    updateField(
                      "quantity",
                      parseInt((e.target as HTMLInputElement).value) || 1,
                    )
                  }
                  class="input input-bordered focus:input-primary w-full max-w-xs"
                  min="1"
                  placeholder="1"
                />
                <label class="label">
                  <span class="label-text-alt">
                    Number of items from selected categories
                  </span>
                </label>
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">Select Categories</span>
                </label>

                <SelectableListComboBox
                  items={selectableCategories.value}
                  selectedIds={props.value.category_ids || []}
                  onSelectionChange={handleSelectionChange}
                  loading={loading.value}
                  placeholder="Search categories..."
                  emptyMessage="No categories available"
                  selectionLabel="category(ies) selected"
                />
              </div>
            </div>
          </div>
        </div>
      );
    },
  });
